import React, { useState, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import BoardActionsMenu from './BoardActionsMenu';

const BoardSwitcher = ({
  boards,
  selectedBoardId,
  showArchived,
  onSelectBoard,
  onCreateBoard,
  onRenameBoard,
  onArchiveBoard,
  onUnarchiveBoard,
  onDeleteBoard,
  onToggleArchived,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const inputRef = useRef(null);

  const visibleBoards = showArchived
    ? boards
    : boards.filter((b) => !b.is_archived);

  const archivedCount = boards.filter((b) => b.is_archived).length;

  const handleAddClick = () => {
    setIsAdding(true);
    setNewBoardName('');
    setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
  };

  const handleAddConfirm = () => {
    const name = newBoardName.trim();
    if (!name) {
      setIsAdding(false);
      return;
    }
    onCreateBoard(name);
    setIsAdding(false);
    setNewBoardName('');
  };

  const handleAddKeyDown = (e) => {
    if (e.key === 'Enter') handleAddConfirm();
    if (e.key === 'Escape') {
      setIsAdding(false);
      setNewBoardName('');
    }
  };

  return (
    <div className="flex items-center gap-1 px-4 pb-2 border-b border-gray-200 flex-wrap">
      {visibleBoards.map((board) => {
        const isActive = board.id === selectedBoardId;
        const isArchived = board.is_archived;
        return (
          <div key={board.id} className="relative group flex items-center">
            <button
              onClick={() => onSelectBoard(board.id)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors mr-0.5
                ${isActive
                  ? 'bg-blue-600 text-white shadow-sm'
                  : isArchived
                  ? 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
              `}
            >
              {board.name}
              {isArchived && (
                <span className="ml-1.5 text-xs opacity-60">(archived)</span>
              )}
            </button>
            <BoardActionsMenu
              board={board}
              onRename={(newName) => onRenameBoard(board.id, newName)}
              onArchive={() => onArchiveBoard(board.id)}
              onUnarchive={() => onUnarchiveBoard(board.id)}
              onDelete={() => onDeleteBoard(board.id)}
            />
          </div>
        );
      })}

      {isAdding ? (
        <div className="flex items-center gap-1">
          <input
            ref={inputRef}
            type="text"
            value={newBoardName}
            onChange={(e) => setNewBoardName(e.target.value)}
            onKeyDown={handleAddKeyDown}
            onBlur={handleAddConfirm}
            placeholder="Board name..."
            className="px-2 py-1 text-sm border border-blue-400 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-36"
          />
          <button
            onMouseDown={(e) => { e.preventDefault(); handleAddConfirm(); }}
            className="p-1 text-blue-600 hover:text-blue-800"
          >
            <Plus size={16} />
          </button>
          <button
            onMouseDown={(e) => { e.preventDefault(); setIsAdding(false); setNewBoardName(''); }}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <button
          onClick={handleAddClick}
          className="px-3 py-1.5 text-sm rounded-lg text-blue-600 hover:bg-blue-50 flex items-center gap-1 font-medium transition-colors"
        >
          <Plus size={14} />
          New Board
        </button>
      )}

      <div className="ml-auto">
        <button
          onClick={onToggleArchived}
          className={`text-xs px-2 py-1 rounded transition-colors ${
            showArchived
              ? 'bg-gray-200 text-gray-700'
              : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          {showArchived ? 'Hide archived' : `Show archived${archivedCount > 0 ? ` (${archivedCount})` : ''}`}
        </button>
      </div>
    </div>
  );
};

export default BoardSwitcher;
